import { call, fork, put, race, SagaGenerator, select, takeMaybe } from "typed-redux-saga";
import { AnyAction } from "redux";

import { DataProvider } from "@com.mgmtp.a12.client/client-core/lib/core/data";
import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { FormActivity } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { DocumentModel, DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { ModelPath } from "@com.mgmtp.a12.base/base-model-api/lib/main/model";

import {
	isDocumentWithProcessMetadata,
	ProcessMetadata,
	ProcessMetadataContainingDocument
} from "../../workflows/data/processMetadata";
import { handleIfOutdated, reloadTaskAndAntragActivity } from "../../../utilities/workflowUtils";
import { mergeData, saveActivity } from "../../../utilities/activityUtils";
import { weakValidation } from "../../../utilities/validationUtils";
import { assertNonNullable, assertTrue } from "../../../utilities/assert";
import { isAttachment } from "../../../utilities/typeGuardUtils";
import { convertEntityInstancePathToModelPath } from "../../../utilities/pathHelpers";
import { getFVManifest } from "../../../app/slices/fachverfahren/selectors";
import { fetchSagaFactory } from "../../../app/sagas/fetchSagaFactory";
import { defaultLocalizer, localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { putErrorNotification } from "../../../utilities/notificationUtils";
import { translateEldoradoCause, extractCause, isRawHttpStatusCause } from "../../../utilities/error";

import {
	ArchiveModalDocument,
	AttachmentInstancePath,
	AttachmentPathDescriptor,
	AttachmentType,
	Configuration,
	EldoradoMetadataWithContextDto,
	isArchiveModalDocumentData,
	isEldoradoMetadataWithContextDto,
	TypedAttachment
} from "../archiveModalTypes";
import { ArchiveModuleFormDescriptor } from "../descriptors";
import { isXakteMetadataOptions, xakteMetadataOptionsRequest, XakteMetadataOptions } from "../xakteApi";
import { addItemToSortingSaga } from "../sagas/addItemToSortingSaga";
import { submitXakteArchive } from "../sagas/xakteArchiveSaga";
import {
	ANNOTATION,
	ATTACHMENT_TYPE,
	CONFIG_KEY,
	ELEMENT_NAME,
	EXTERNAL_ENUM_CACHE_KEY,
	INITIAL_ATTACHMENTS_SELECTION,
	PAPIERFORM,
	PATH,
	SEGMENT_LABELS,
	SEGMENT_TYPE,
	STRING_VALUE
} from "../constants";

const logger = LoggerFactory.getLogger("ArchiveModuleFormDataProvider");
const documentService = new DocumentServiceFactory().getDocumentService();
export const archiveModuleFormDataProvider: DataProvider = {
	name: "ArchiveModuleFormDataProvider",
	canHandle({ dataHolder, operation }) {
		return ArchiveModuleFormDescriptor.isInstance(dataHolder.descriptor) && ["save", "load"].includes(operation);
	},
	*provideData({ activityId, operation }) {
		assertTrue(operation === "load" || operation === "save");

		const activity = yield* select(ActivitySelectors.activityById(activityId));
		assertTrue(ArchiveModuleFormDescriptor.isInstance(activity?.descriptor));
		const { sourceActivityId, instance, archiveSystem } = activity.descriptor;

		if (operation === "load") {
			yield* call(handleLoad, activityId, sourceActivityId, instance, archiveSystem);
		} else {
			yield* call(handleSave, sourceActivityId, activityId, archiveSystem);
		}
	}
};

type ArchiveSystem = ArchiveModuleFormDescriptor["archiveSystem"];
type ArchiveMetadataContext =
	| { archiveSystem: "eldorado"; data: EldoradoMetadataWithContextDto }
	| { archiveSystem: "xakte"; data: XakteMetadataOptions };

function* handleLoad(
	activityId: string,
	sourceActivityId: string,
	docRef: string,
	archiveSystem: ArchiveSystem
): SagaGenerator<void> {
	try {
		const sourceActivity = yield* select(ActivitySelectors.activityById(sourceActivityId));
		assertNonNullable(sourceActivity);

		const isValid = yield* call(weakValidation, sourceActivityId);
		if (!isValid) {
			logger.error(`Document is not valid from activity: ${sourceActivityId}.`);
			yield* put(ActivityActions.cancel({ activityId }));
			return;
		}

		const isSaved = yield* call(saveActivity, sourceActivityId);
		if (!isSaved) {
			logger.error(`Could not save activity: ${sourceActivityId}.`);
			yield* put(ActivityActions.cancel({ activityId }));
			return;
		}
		yield* call(reloadTaskAndAntragActivity);

		const data = yield* select(ActivitySelectors.data(sourceActivityId));

		if (
			!data ||
			!FormActivity.Data.SingleDocumentData.isInstance(data) ||
			!isDocumentWithProcessMetadata(data.document) ||
			!data.document.root.processMetadata.vorgangsnummer
		) {
			return;
		}

		const configuration = yield* call(resolveConfigurations, data.document.root.processMetadata.fachverfahren.id);
		const attachments = yield* call(resolveAttachments, sourceActivityId, configuration, data.document);
		const metadataContext = yield* call(fetchMetadataContext, docRef, activityId, archiveSystem);
		if (metadataContext === undefined) {
			yield* put(ActivityActions.setData({ activityId, data: { ...data } }));
			return;
		}
		const document = createInitialData(
			data.document.root.processMetadata,
			attachments,
			configuration,
			metadataContext
		);

		yield* put(ActivityActions.setData({ activityId, data: { document } }));
		yield* race({
			push: fork(addItemToSortingSaga, attachments, activityId),
			cancel: takeMaybe(
				(it: AnyAction) => ActivityActions.cancel.match(it) && it.payload.activityId === activityId
			)
		});
	} catch (error) {
		logger.error("Error loading archive module configuration form data", error);
	}
}

function* resolveConfigurations(appId: string): SagaGenerator<Configuration> {
	const fvManifest = yield* select(getFVManifest(appId));
	const requireAllApplicationAttachments =
		fvManifest?.configurationValues.find(it => it.key === CONFIG_KEY.INCLUDE_ALL_ATTACHMENTS)?.value ===
		STRING_VALUE.TRUE;
	const initialAttachmentsSelection =
		fvManifest?.configurationValues.find(it => it.key === CONFIG_KEY.INITIAL_ATTACHMENTS_SELECTION)?.value ??
		INITIAL_ATTACHMENTS_SELECTION.ALL;
	const sortingConfiguration = fvManifest?.configurationValues.find(it => it.key === CONFIG_KEY.SORTIERUNG)?.value;
	const hydratedSorting = sortingConfiguration
		? (JSON.parse(sortingConfiguration) as string[])
		: Object.keys(SEGMENT_TYPE);
	const isCompound =
		fvManifest?.configurationValues.find(it => it.key === CONFIG_KEY.VERBUND_DATEI)?.value === STRING_VALUE.TRUE;
	const allowCustomSegmentSorting =
		fvManifest?.configurationValues.find(it => it.key === CONFIG_KEY.ALLOW_CUSTOM_SEGMENT_SORTING)?.value ===
		STRING_VALUE.TRUE;
	const defaultAktenzeichen = fvManifest?.configurationValues?.find(it => it.key === "AKTENZEICHEN")?.value;
	return {
		requireAllApplicationAttachments,
		sorting: hydratedSorting,
		isCompound,
		allowCustomSegmentSorting,
		initialAttachmentsSelection:
			initialAttachmentsSelection === INITIAL_ATTACHMENTS_SELECTION.PRESELECTED
				? INITIAL_ATTACHMENTS_SELECTION.PRESELECTED
				: initialAttachmentsSelection === INITIAL_ATTACHMENTS_SELECTION.UNSELECTED
					? INITIAL_ATTACHMENTS_SELECTION.UNSELECTED
					: INITIAL_ATTACHMENTS_SELECTION.ALL,
		defaultAktenzeichen
	};
}

function createInitialData(
	processMetadata: ProcessMetadata,
	attachments: TypedAttachment[],
	configuration: Configuration,
	metadataContext: ArchiveMetadataContext
): ArchiveModalDocument {
	const eldoradoContext = metadataContext.archiveSystem === "eldorado" ? metadataContext.data : undefined;
	const xakteContext = metadataContext.archiveSystem === "xakte" ? metadataContext.data : undefined;
	const eldoradoMetadata = processMetadata.eldorado;
	const { sorting, isCompound, defaultAktenzeichen } = configuration;

	const allowedExtensions: Set<string> =
		eldoradoContext?.allowedAttachmentExtensions && eldoradoContext.allowedAttachmentExtensions.length > 0
			? new Set(eldoradoContext.allowedAttachmentExtensions)
			: (() => {
					if (metadataContext.archiveSystem === "xakte") {
						return new Set();
					}
					throw new Error(
						"allowedAttachmentExtensions not provided by backend (de.skhh.modulf.docconversion.allowedExtensions). " +
							"Check server configuration."
					);
				})();

	const isAttachmentExtensionAllowed = (filename: string | null | undefined): boolean => {
		if (metadataContext.archiveSystem === "xakte") {
			return true;
		}
		if (!filename) {
			return false;
		}
		const dotIndex = filename.lastIndexOf(".");
		const ext = dotIndex >= 0 ? filename.substring(dotIndex + 1).toLowerCase() : "";
		return allowedExtensions.has(ext);
	};
	const initialAttachments = resolveInitialAttachments(attachments, configuration, isAttachmentExtensionAllowed);
	const selectedInitialAttachmentIds = resolveSelectedInitialAttachmentIds(
		initialAttachments,
		configuration.initialAttachmentsSelection
	);
	const dokartOptions =
		eldoradoContext?.docArtValues
			?.filter((it): it is { text: string; alias: string } => it.text !== undefined && it.alias !== undefined)
			.map(it => ({ id: it.alias, value: it.text })) ??
		xakteContext?.options.dokumentenArt?.map(it => ({ id: it.value, value: it.label })) ??
		[];

	const isPolizei = eldoradoContext?.isPolizei ?? false;

	const absenderOptions =
		eldoradoContext?.absenderValues
			?.filter((it): it is { text: string; alias: string } => it.text !== undefined && it.alias !== undefined)
			// Polizei: show display name, submit alias → id=alias, value=text
			// ABS (non-Polizei): show display name, submit display name → id=text, value=text
			.map(it => (isPolizei ? { id: it.alias, value: it.text } : { id: it.text, value: it.text })) ??
		xakteContext?.options.absender?.map(it => ({ id: it.value, value: it.label })) ??
		[];

	const betreff =
		eldoradoMetadata?.betreff ??
		eldoradoContext?.betreff ??
		stringDefault(xakteContext, "betreff") ??
		processMetadata.vorgangsnummer ??
		"";
	const bezug = eldoradoMetadata?.bezug ?? eldoradoContext?.bezug ?? stringDefault(xakteContext, "bezug") ?? "";

	const duplicatedMessage = eldoradoContext?.duplicatedItemTexts?.join("\n | \n") ?? "";

	return {
		root: {
			aktenkennzeichen:
				eldoradoMetadata?.aktenkennzeichen ??
				stringDefault(xakteContext, "aktenkennzeichen") ??
				defaultAktenzeichen ??
				"",
			mappenNummer: eldoradoMetadata?.mappenNummer ?? stringDefault(xakteContext, "mappenNummer") ?? "",
			betreff,
			absender: eldoradoMetadata?.absender ?? stringDefault(xakteContext, "absender") ?? "",
			bezug,
			dokumentenArt: eldoradoMetadata?.dokumentenArt ?? stringDefault(xakteContext, "dokumentenArt") ?? "",
			papierform: eldoradoMetadata?.papierform ?? stringDefault(xakteContext, "papierform") ?? PAPIERFORM.NEIN,
			externalEnumCache: [
				{
					key: EXTERNAL_ENUM_CACHE_KEY.ARCHIVE_INITIAL_ATTACHMENT_OPTIONS,
					options: initialAttachments.map(it => ({
						value: it.attachment.internal_filename ?? "",
						id: it.attachment.attachment_id ?? ""
					}))
				},
				{
					key: EXTERNAL_ENUM_CACHE_KEY.ARCHIVE_ATTACHMENT_OPTIONS,
					options: attachments
						.filter(it => it.type !== ATTACHMENT_TYPE.INITIAL)
						.filter(it => isAttachmentExtensionAllowed(it.attachment.original_filename))
						.map(it => it.attachment)
						.map(it => ({ value: it.internal_filename ?? "", id: it.attachment_id ?? "" }))
				},
				{
					key: EXTERNAL_ENUM_CACHE_KEY.ARCHIVE_PAPIERFORM_OPTIONS,
					options: xakteContext?.options.papierform?.map(it => ({ id: it.value, value: it.label })) ?? [
						{ id: PAPIERFORM.NEIN, value: "Nein" },
						{ id: PAPIERFORM.JA, value: "Ja" }
					]
				},
				{
					key: EXTERNAL_ENUM_CACHE_KEY.ARCHIVE_SENDER_OPTIONS,
					options: absenderOptions
				},
				{
					key: EXTERNAL_ENUM_CACHE_KEY.ARCHIVE_DOKART_OPTIONS,
					options: dokartOptions
				}
			],
			initialAttachments: initialAttachments
				.filter(it => selectedInitialAttachmentIds.has(it.attachment.attachment_id ?? ""))
				.map(it => ({ value: it.attachment.attachment_id ?? "" })),
			sortierungInitialAttachments: configuration.sorting.includes(SEGMENT_TYPE.INITIALE_ATTACHMENTS)
				? attachments
						.filter(it => it.type === ATTACHMENT_TYPE.INITIAL)
						.filter(
							it =>
								!it.initiallyAttached ||
								selectedInitialAttachmentIds.has(it.attachment.attachment_id ?? "")
						)
						.map(it => ({
							id: it.attachment.attachment_id ?? "",
							label: it.attachment.internal_filename ?? ""
						}))
				: [],
			sortierungSegments: configuration.allowCustomSegmentSorting
				? sorting.map(it => ({ id: it, label: SEGMENT_LABELS[it] }))
				: [],
			metadata: {
				containsInitialAttachments: sorting.includes(SEGMENT_TYPE.INITIALE_ATTACHMENTS),
				containsAttachments: sorting.includes(SEGMENT_TYPE.HINZUGEFUEGTE_ATTACHMENTS),
				containsGeneratedDocuments: sorting.includes(SEGMENT_TYPE.GENERIERTE_DOKUMENTE) && isCompound,
				allowCustomSegmentSorting: configuration.allowCustomSegmentSorting,
				forceInitialAttachments:
					configuration.initialAttachmentsSelection === INITIAL_ATTACHMENTS_SELECTION.ALL,
				duplicatedItemTexts: duplicatedMessage,
				busy: false
			}
		}
	} satisfies ArchiveModalDocument;
}

function stringDefault(metadataOptions: XakteMetadataOptions | undefined, key: string): string | undefined {
	const value = metadataOptions?.defaults[key];
	return typeof value === "string" ? value : undefined;
}

export function resolveInitialAttachments(
	attachments: TypedAttachment[],
	configuration: Configuration,
	isAttachmentExtensionAllowed: (filename: string | null | undefined) => boolean
): TypedAttachment[] {
	const allInitialAttachments = attachments.filter(it => it.type === ATTACHMENT_TYPE.INITIAL);
	return configuration.initialAttachmentsSelection === INITIAL_ATTACHMENTS_SELECTION.ALL
		? allInitialAttachments
		: allInitialAttachments.filter(it => isAttachmentExtensionAllowed(it.attachment.original_filename));
}

export function resolveSelectedInitialAttachmentIds(
	initialAttachments: TypedAttachment[],
	initialAttachmentsSelection: Configuration["initialAttachmentsSelection"]
): Set<string> {
	return new Set(
		initialAttachments
			.filter(
				it => !it.initiallyAttached || initialAttachmentsSelection !== INITIAL_ATTACHMENTS_SELECTION.UNSELECTED
			)
			.map(it => it.attachment.attachment_id ?? "")
	);
}

function* fetchMetadataContext(
	documentReference: string,
	activityId: string,
	archiveSystem: ArchiveSystem
): SagaGenerator<ArchiveMetadataContext | undefined> {
	let result: ArchiveMetadataContext | undefined;

	yield* call(
		fetchSagaFactory(
			archiveSystem === "eldorado"
				? {
						relativeUrl: `/eldorado/metadataValues?docRef=${documentReference}`,
						method: "GET"
					}
				: xakteMetadataOptionsRequest(documentReference),
			function* handleResponse(response) {
				if (response.ok) {
					const data = yield* call(async () => response.json());
					if (archiveSystem === "eldorado" && isEldoradoMetadataWithContextDto(data)) {
						result = { archiveSystem, data };
					} else if (archiveSystem === "xakte" && isXakteMetadataOptions(data)) {
						result = { archiveSystem, data };
					} else {
						throw new Error(`Response is not valid ${archiveSystem} metadata.`);
					}
				}
			},
			function* handleError(error: unknown) {
				logger.error(error);
				const rawCause = extractCause(error);
				const cause =
					rawCause !== undefined
						? archiveSystem === "eldorado"
							? translateEldoradoCause(rawCause)
							: rawCause
						: undefined;
				const errorMessage =
					archiveSystem === "eldorado"
						? cause !== undefined && !isRawHttpStatusCause(cause)
							? localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.eldorado.metadataErrorWithCause,
									{ cause: { type: "plain", value: cause } }
								)
							: localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.eldorado.transmittingError
								)
						: cause !== undefined && !isRawHttpStatusCause(cause)
							? localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.xakte.metadataErrorWithCause,
									{ cause: { type: "plain", value: cause } }
								)
							: localizableFromLocaleResources(
									RESOURCE_KEYS.features.notification.error.xakte.metadataError
								);
				yield* call(putErrorNotification, errorMessage, error, activityId);
				yield* call(mergeData, activityId, {
					status: "error",
					message: (defaultLocalizer(errorMessage) ?? "").trim(),
					busy: false
				});
			}
		)
	);

	return result;
}

function* handleSave(sourceActivityId: string, activityId: string, archiveSystem: ArchiveSystem): SagaGenerator<void> {
	if (yield* call(handleIfOutdated)) {
		return;
	}
	const data = yield* select(ActivitySelectors.data(activityId));
	assertTrue(isArchiveModalDocumentData(data));
	if (archiveSystem === "xakte") {
		const previewId = data.document.root.metadata?.jobUuid;
		assertNonNullable(previewId);
		yield* call(submitXakteArchive, activityId, sourceActivityId, previewId);
		return;
	}

	yield* call(
		fetchSagaFactory(
			{
				relativeUrl: `/eldorado/completeArchiving?conversionId=${data.document.root.metadata?.jobUuid ?? ""}`,
				method: "POST"
			},
			function* handleResponse(response) {
				if (response.ok) {
					const sourceActivity = yield* select(ActivitySelectors.activityById(sourceActivityId));
					if (sourceActivity) {
						yield* call(reloadTaskAndAntragActivity);
					}

					yield* call(mergeData, activityId, { busy: false });
					yield* put(ActivityActions.cancel({ activityId }));
				}
			},
			function* handleError(error: unknown) {
				logger.error(error);
				const rawCause = extractCause(error);
				const cause = rawCause !== undefined ? translateEldoradoCause(rawCause) : undefined;
				const errorMessage =
					cause !== undefined && !isRawHttpStatusCause(cause)
						? localizableFromLocaleResources(
								RESOURCE_KEYS.features.notification.error.eldorado.transmittingErrorWithCause,
								{ cause: { type: "plain", value: cause } }
							)
						: localizableFromLocaleResources(
								RESOURCE_KEYS.features.notification.error.eldorado.transmittingError
							);
				yield* call(putErrorNotification, errorMessage, error, activityId);
				yield* call(mergeData, activityId, {
					status: "error",
					message: (defaultLocalizer(errorMessage) ?? "").trim(),
					busy: false
				});
			}
		)
	);
}

function* resolveAttachments(
	sourceActivityId: string,
	configuration: Configuration,
	document: ProcessMetadataContainingDocument
): SagaGenerator<TypedAttachment[]> {
	// Find attachments
	const sourceDocumentModel = yield* select(
		ModelSelectors.modelInScene({ activityId: sourceActivityId, modelType: "document" })
	);
	assertNonNullable(sourceDocumentModel);
	assertTrue(Model.isDocumentModel(sourceDocumentModel));

	const rootGroup = sourceDocumentModel.content.modelRoot.elements.find(it => it.name === ELEMENT_NAME.ROOT);
	assertNonNullable(rootGroup);
	const attachmentGroupPaths = collectAttachmentGroupPaths(rootGroup as DocumentModel.Group, [
		{ elementName: ELEMENT_NAME.ROOT }
	]);

	const attachmentGroupInstanceSearchPaths: AttachmentInstancePath[] = attachmentGroupPaths.map(descriptor => ({
		path: descriptor.modelPath.map(component => ({ ...component, index: 0 })),
		descriptor
	}));
	const attachmentGroupInstancePaths: AttachmentInstancePath[] = attachmentGroupInstanceSearchPaths.flatMap(it =>
		documentService.getEntityPathsInDocument(document, it.path).map(path => ({ path, descriptor: it.descriptor }))
	);

	const attachments: TypedAttachment[] = [];
	for (const attachmentGroupInstancePath of attachmentGroupInstancePaths) {
		collectAttachments(
			document,
			attachmentGroupInstancePath,
			configuration.requireAllApplicationAttachments,
			attachments,
			configuration.sorting
		);
	}
	return attachments;
}

function collectAttachmentGroupPaths(
	group: DocumentModel.Group,
	path: ModelPath,
	foundAttachmentGroupPaths: AttachmentPathDescriptor[] = []
): AttachmentPathDescriptor[] {
	for (const child of group.elements) {
		if (child.type === "Group") {
			if (child.usageType === "attachment") {
				const attachmentType: AttachmentType = child.annotations?.find(
					it =>
						it.name === ANNOTATION.MF_RUNTIME_EXTERNAL_ENUMERATION &&
						it.value === ANNOTATION.GENERIERTES_DOKUMENT
				)
					? ATTACHMENT_TYPE.GENERIERT
					: ATTACHMENT_TYPE.ATTACHMENT;
				foundAttachmentGroupPaths.push({
					modelPath: [...path, { elementName: child.name }],
					type: attachmentType
				});
			} else {
				collectAttachmentGroupPaths(child, [...path, { elementName: child.name }], foundAttachmentGroupPaths);
			}
		}
	}
	return foundAttachmentGroupPaths;
}

function collectAttachments(
	document: ProcessMetadataContainingDocument,
	attachmentGroupInstancePath: AttachmentInstancePath,
	requireAllApplicationAttachments: boolean,
	attachments: TypedAttachment[],
	sorting: string[]
) {
	const attachmentGroup = documentService.getAssignedObject(document, attachmentGroupInstancePath.path);
	if (isAttachment(attachmentGroup)) {
		// Make sure this isn't an initial attachment, which we are not supposed to show here
		const isInitialAttachment = documentService.getAssignedObject(document, [
			...attachmentGroupInstancePath.path.slice(0, -1),
			{
				elementName: ELEMENT_NAME.INITIAL_ANGEHAENGT,
				index: 1
			}
		]);
		const groupPath = convertEntityInstancePathToModelPath(attachmentGroupInstancePath.path);
		if (!isInitialAttachment && (!requireAllApplicationAttachments || !groupPath.startsWith(PATH.ROOT_ANTRAG))) {
			if (isConfiguredAllowedContent(attachmentGroupInstancePath.descriptor, sorting)) {
				attachments.push({
					attachment: attachmentGroup,
					...attachmentGroupInstancePath.descriptor,
					initiallyAttached: false
				});
			}
		} else {
			attachments.push({
				attachment: attachmentGroup,
				type: ATTACHMENT_TYPE.INITIAL,
				initiallyAttached: Boolean(isInitialAttachment)
			});
		}
	}
}

function isConfiguredAllowedContent(attachmentDescriptor: AttachmentPathDescriptor, sorting: string[]) {
	const segmentLabel =
		attachmentDescriptor.type === ATTACHMENT_TYPE.GENERIERT
			? SEGMENT_TYPE.GENERIERTE_DOKUMENTE
			: SEGMENT_TYPE.HINZUGEFUEGTE_ATTACHMENTS;
	return sorting.includes(segmentLabel);
}
